import React, { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, BookOpen, Clock, TrendingUp, ArrowRight, BarChart3, Sigma } from "lucide-react";
import { CHARTISTE_ARTICLES } from "@/pages/AcademyChartistes";
import { EXTRA_TRACKS_ARTICLES, EXTRA_TRACKS_META } from "@/pages/AcademyExtraTracks";
import { EXTRA_TRACKS_V2_ARTICLES, EXTRA_TRACKS_V2_META } from "@/pages/AcademyExtraTracksV2";
import ShareBar from "@/components/ShareBar";
import usePageMeta from "@/hooks/usePageMeta";

// Combine all extra tracks metadata + articles for uniform handling
const ALL_EXTRA_META = [...EXTRA_TRACKS_META, ...EXTRA_TRACKS_V2_META];
const ALL_EXTRA_ARTICLES = [...EXTRA_TRACKS_ARTICLES, ...EXTRA_TRACKS_V2_ARTICLES];

/**
 * ANATRADEX Academy — Pedagogical articles for beginners → semi-pros.
 * Public routes:
 *   /academy         → grid of all articles
 *   /academy/:slug   → single article view
 */

const ARTICLES = [
  // Track par défaut = "quant" (algo/statistique). Les articles suivants n'ont pas de track = "quant".
  {
    slug: "trading-algorithmique-101",
    level: "Débutant",
    minutes: 6,
    title: "Trading algorithmique : les 5 concepts fondamentaux",
    summary: "Signal, backtest, drawdown, risk/reward, edge. Le vocabulaire minimum pour ne plus se perdre.",
    content: (
      <>
        <p>Avant de toucher au premier outil quantitatif, comprenez ces 5 briques :</p>
        <h3>1. Le signal</h3>
        <p>Un signal, c&apos;est une <b>décision codifiée</b> : « acheter EURUSD à 1.0834, stop à 1.0810, cible à 1.0890 ». Pas une opinion, pas un feeling. Une règle exécutable. Sur ANATRADEX, chaque signal contient <b>direction, entry, stop-loss, take-profit, confiance</b>.</p>
        <h3>2. Le backtest</h3>
        <p>Simuler votre stratégie sur l&apos;historique. Attention : un backtest génial ne garantit rien — il peut être <b>overfitté</b> (adapté par hasard aux données passées). D&apos;où l&apos;importance du walk-forward, du PBO et du Deflated Sharpe (voir articles dédiés).</p>
        <h3>3. Le drawdown (DD)</h3>
        <p>La perte maximale entre un pic d&apos;équité et le creux qui suit. Un DD de 40 % veut dire qu&apos;après un pic à $10k, votre compte est descendu à $6k avant de remonter. <b>Aucun système sérieux ne cache son DD</b>.</p>
        <h3>4. Le ratio risk/reward (R:R)</h3>
        <p>Combien vous gagnez par unité risquée. R:R = 2 → vous gagnez $200 quand vous êtes bon, vous perdez $100 quand vous êtes mauvais. Avec 40 % de trades gagnants et R:R = 2, vous êtes profitable.</p>
        <h3>5. L&apos;edge</h3>
        <p>Votre avantage statistique <b>net de frais</b>. Pas votre feeling. Pas votre confiance. Le résultat démontré par un backtest robuste avec p-value {"<"} 5 %.</p>
        <p><b>Retenez :</b> tout ce qui suit sur ANATRADEX — ML, order flow, régime detection — sert à identifier et valider un <em>edge</em>. C&apos;est tout.</p>
      </>
    ),
  },
  {
    slug: "lire-un-signal-zii-dp-rl",
    level: "Débutant",
    minutes: 5,
    title: "Comment lire un signal ZII / DP / RL",
    summary: "Le vocabulaire propriétaire d'ANATRADEX, expliqué en 5 minutes.",
    content: (
      <>
        <p>ANATRADEX assume ouvertement son inspiration : nos 5 termes désignent nos implémentations algorithmiques de concepts popularisés par ICT (Inner Circle Trader) / Smart Money. Notre valeur ajoutée n&apos;est pas la méthode — c&apos;est son automatisation et sa validation statistique (PBO, DSR, permutation) :</p>
        <h3>ZII — <em>Zone d&apos;Inefficience Institutionnelle</em></h3>
        <p>Une zone de prix où de gros ordres institutionnels ont laissé un « déséquilibre » — le prix y revient souvent. Équivalent : Fair Value Gap (FVG). <b>Signal fort quand le prix touche la ZII avec un régime favorable</b>.</p>
        <h3>DP — <em>Displacement Pattern</em></h3>
        <p>Une bougie de forte amplitude qui « déplace » le prix hors de son range habituel. Marque souvent le début d&apos;une expansion directionnelle. <b>Un DP suivi d&apos;un retour dans une ZII = setup à haut R:R</b>.</p>
        <h3>RL — <em>Reference Level</em></h3>
        <p>Niveau clé (high/low de la journée précédente, open Asian session, extrêmes de swing). Utilisé pour placer les stops et les cibles. <b>Toujours vérifier que votre stop est de l&apos;autre côté d&apos;un RL</b>.</p>
        <h3>CSD — <em>Cross-Session Divergence</em></h3>
        <p>Divergence entre le comportement du prix pendant deux sessions consécutives (Asian → London par ex.). Signal de continuation ou renversement selon le contexte de régime.</p>
        <h3>CRT — <em>Consolidation Range Trigger</em></h3>
        <p>Cassure validée d&apos;une phase de consolidation, avec volume confirmé. Le point d&apos;entrée « classique » pour du swing.</p>
        <p><b>Astuce :</b> chaque signal ANATRADEX indique lesquels de ces éléments ont contribué à la décision. Regardez la section « reasons » pour comprendre <em>pourquoi</em> le signal a été émis.</p>
      </>
    ),
  },
  {
    slug: "backtest-piege-overfitting",
    level: "Semi-pro",
    minutes: 8,
    title: "Le piège n°1 des backtests : l'overfitting",
    summary: "Pourquoi 90 % des backtests retail sont invalides, et comment ANATRADEX le corrige.",
    content: (
      <>
        <p><b>L&apos;overfitting</b>, c&apos;est le péché mortel du quant amateur : optimiser une stratégie sur des données historiques jusqu&apos;à obtenir des résultats spectaculaires… qui s&apos;effondrent en live.</p>
        <h3>Le symptôme classique</h3>
        <p>Sharpe 5+, drawdown minuscule, 80 % win rate. Le rêve. En réalité : la stratégie a appris le <b>bruit spécifique</b> de vos données de test, pas un edge réel.</p>
        <h3>Pourquoi c&apos;est si courant</h3>
        <p>Vous testez 100 combinaisons de paramètres. Par pur hasard, une donne un Sharpe de 4. Vous la choisissez. C&apos;est du <b>data snooping / multiple testing bias</b>. Statistiquement, avec 100 essais aléatoires, obtenir 1 Sharpe {">"} 3 est banal.</p>
        <h3>Les 3 outils sérieux pour corriger</h3>
        <ol>
          <li><b>Walk-Forward</b> : optimiser sur les 60 % premiers, tester sur les 40 % suivants sans y toucher. Si le Sharpe s&apos;effondre → overfitting confirmé.</li>
          <li><b>Purged K-Fold Cross-Validation</b> (López de Prado) : découper l&apos;historique en morceaux et vérifier la stabilité de l&apos;edge, avec une zone tampon (« purge ») entre train/test pour éviter le look-ahead bias.</li>
          <li><b>Probability of Backtest Overfitting (PBO)</b> : calcule mathématiquement la probabilité que votre stratégie soit surajustée. <b>Objectif : PBO {"<"} 30 %</b>. Au-delà = poubelle.</li>
        </ol>
        <h3>Ce qu&apos;ANATRADEX affiche automatiquement</h3>
        <p>Sur la <Link to="/audit" className="text-accent-blue underline">page Audit</Link> et dans le DD Pack : PBO, Deflated Sharpe (qui pénalise le multiple testing), permutation p-value. Si un de ces indicateurs est mauvais, <b>le trust badge devient jaune, pas vert</b>. Honnêteté par design.</p>
      </>
    ),
  },
  {
    slug: "deflated-sharpe-explique",
    level: "Semi-pro",
    minutes: 7,
    title: "Le Deflated Sharpe : comprendre en 5 minutes",
    summary: "Pourquoi le Sharpe classique ment, et ce que Bailey & López de Prado ont corrigé.",
    content: (
      <>
        <p>Le <b>Sharpe Ratio</b> mesure votre rendement excédentaire par unité de risque. Formule : <code>(rendement - risk-free) / écart-type</code>. Plus c&apos;est élevé, mieux c&apos;est. En théorie.</p>
        <h3>Le problème</h3>
        <p>Le Sharpe classique <b>ne prend PAS en compte</b> :</p>
        <ul>
          <li>Combien de stratégies vous avez testées avant celle-ci (biais de sélection)</li>
          <li>La skewness (asymétrie) de vos rendements — les stratégies qui explosent violemment mais rarement</li>
          <li>La kurtosis (fat tails) — les jours de black swan</li>
        </ul>
        <p>Résultat : un trader qui teste 100 stratégies et publie la meilleure aura un Sharpe apparent flatteur, mais statistiquement <b>ce n&apos;est probablement que du bruit</b>.</p>
        <h3>La correction Bailey & López de Prado</h3>
        <p>Le <b>Deflated Sharpe Ratio (DSR)</b> répond à la question : <em>« Sachant que j&apos;ai testé N stratégies, quelle est la probabilité que mon Sharpe observé soit réel et pas du hasard ? »</em></p>
        <p>La formule complète est technique (référence : Bailey & López de Prado, 2014), mais l&apos;idée est simple :</p>
        <ol>
          <li>Estimer le Sharpe attendu par pur hasard sous H₀ (aucun edge), en fonction du nombre d&apos;essais et de la longueur historique</li>
          <li>Ajuster pour skewness et kurtosis</li>
          <li>Calculer la probabilité que le Sharpe observé dépasse ce seuil</li>
        </ol>
        <h3>Comment le lire sur ANATRADEX</h3>
        <p>Sur le trust badge en haut de la landing :</p>
        <ul>
          <li><b>DSR {">"} 95 %</b> = edge très probablement réel (vert)</li>
          <li>DSR 80–95 % = à surveiller (jaune)</li>
          <li>DSR {"<"} 80 % = du bruit statistique (rouge, pas d&apos;alloc réelle)</li>
        </ul>
        <p><b>Retenir :</b> le DSR est la seule métrique qui résiste à l&apos;examen d&apos;une prop firm ou d&apos;un investisseur sérieux. Le Sharpe brut = déclaration marketing, le DSR = preuve.</p>
      </>
    ),
  },
  {
    slug: "prop-firm-erreurs-a-eviter",
    level: "Semi-pro",
    minutes: 6,
    title: "Prop firm challenge : les 6 erreurs qui vous font échouer",
    summary: "FTMO, Topstep, MyForexFunds… ce qui distingue ceux qui passent de ceux qui blowent.",
    content: (
      <>
        <p>Les prop firm challenges ont une <b>base rate de réussite {"<"} 10 %</b>. Voici pourquoi la plupart échouent — et comment ANATRADEX aide à contourner chaque piège.</p>
        <h3>Erreur 1 — Ignorer le daily loss limit</h3>
        <p>Chez FTMO : 5 % max par jour. Vous perdez 4,9 %, vous êtes techniquement OK mais le lendemain, un mauvais trade et vous êtes disqualifié. <b>ANATRADEX trackr en temps réel votre distance au daily limit</b> avec un compteur visuel.</p>
        <h3>Erreur 2 — Overtrading en fin de challenge</h3>
        <p>Vous êtes à +8 % sur objectif de 10 %, il vous reste 5 jours. Vous forcez. Vous perdez. Classic. Le <b>Prop Firm tracker</b> d&apos;ANATRADEX affiche « minimum daily gains needed » pour finir sans stress.</p>
        <h3>Erreur 3 — Risque de position mal calibré</h3>
        <p>Vous risquez 2 % par trade au lieu du 0,5-1 % recommandé pour un capital de challenge. Un 4-perdants d&apos;affilée = 8 % = fail. Utilisez le <Link to="/app/sizer" className="text-accent-blue underline">Position Sizer</Link>.</p>
        <h3>Erreur 4 — Trader la news sans hedging</h3>
        <p>NFP à 14h30. Vous êtes long EURUSD. Spread × 20. Slippage massif. ANATRADEX affiche les événements macro à venir dans le panneau de chaque signal.</p>
        <h3>Erreur 5 — Prendre les signaux « blocked »</h3>
        <p>Notre analyzer marque certains signaux comme <code>blocked: true</code> quand le régime de marché ne les favorise pas. <b>Respectez ce flag</b>. Ce sont les trades qui coûtent le plus cher.</p>
        <h3>Erreur 6 — Pas de journal + pas de post-mortem</h3>
        <p>Chaque trade doit être enregistré dans votre <Link to="/app/profile" className="text-accent-blue underline">journal</Link>. Sans ça, vous répétez les mêmes erreurs. Utilisez la section Post-Mortem hebdomadaire.</p>
        <p><b>Bonus :</b> ANATRADEX Institutional ($199/mois) inclut le module <em>Prop Firm Simulator</em> — reproduit exactement les règles FTMO/Topstep/MFF pendant un mois sur compte demo avant de payer le vrai challenge.</p>
      </>
    ),
  },
  {
    slug: "order-flow-pour-debutants",
    level: "Semi-pro",
    minutes: 7,
    title: "Order Flow Level-2 : le décoder en 5 minutes",
    summary: "OFI, imbalance, VPOC, micro-price. Le vocabulaire des market makers, accessible.",
    content: (
      <>
        <p>Le prix affiché à l&apos;écran ne raconte que <b>10 % de l&apos;histoire</b>. Les 90 % restants vivent dans le carnet d&apos;ordres (Level-2). Voici les métriques essentielles.</p>
        <h3>Le carnet d&apos;ordres (Order Book)</h3>
        <p>Liste de tous les ordres en attente : côté acheteur (bids) et côté vendeur (asks), avec leur prix et leur volume. Habituellement 10 niveaux de chaque côté.</p>
        <h3>OFI — Order Flow Imbalance</h3>
        <p>Différence nette entre pression à l&apos;achat et à la vente au top of book. <b>OFI positif = pression achats {">"} vente</b>. Formule : Cont-Kukanov-Stoikov (2013). Un OFI cumulé qui diverge du prix = signal précoce de reversion ou d&apos;expansion.</p>
        <h3>Imbalance (top of book)</h3>
        <p><code>(bid_size - ask_size) / (bid_size + ask_size)</code>. Va de -1 (vendeurs dominent) à +1 (acheteurs dominent). Sur ANATRADEX : le panneau Order Book affiche l&apos;imbalance temps réel.</p>
        <h3>Micro-price</h3>
        <p>Prix « pondéré » par les tailles bid/ask. Formule : <code>(bid × ask_size + ask × bid_size) / total</code>. Plus proche de la vérité que le mid quand il y a un fort déséquilibre.</p>
        <h3>VPOC / VAH / VAL (Volume Profile)</h3>
        <p>Le <b>Volume Point of Control (VPOC)</b> est le prix où le plus de volume s&apos;est échangé sur une période. Les <b>Value Area High/Low</b> encadrent 70 % du volume. <b>Les prix reviennent vers le VPOC comme un aimant</b>.</p>
        <h3>Absorption</h3>
        <p>Quand un gros vendeur (ou acheteur) absorbe un mouvement sans céder le niveau. Signal souvent précédent d&apos;un renversement. ANATRADEX flag automatiquement.</p>
        <h3>Comment l&apos;utiliser en pratique</h3>
        <p>Avant chaque trade : (1) le prix est-il au-dessus ou sous le VPOC ? (2) L&apos;OFI cumulé est-il en accord avec votre direction ? (3) Y a-t-il de l&apos;absorption au niveau clé ? Trois oui = trade à haute probabilité.</p>
        <p><em>Note honnête</em> : ANATRADEX fonctionne actuellement en <b>mode synthetic Cont-Kukanov-Stoikov</b> (order book simulé réaliste). Pour de la vraie Level-2 Databento MBP-10 ou Polygon NBBO, il faut configurer <code>DATABENTO_API_KEY</code> ou <code>POLYGON_API_KEY</code> dans les paramètres.</p>
      </>
    ),
  },
  {
    slug: "kelly-fractionnaire-position-sizing",
    level: "Semi-pro",
    minutes: 6,
    title: "Kelly fractionnaire : la taille de position optimale",
    summary: "La formule qui a fait la fortune de Bill Gross et Ed Thorp. Simplifiée.",
    content: (
      <>
        <p>La question qui tue plus de traders que n&apos;importe quelle stratégie : <b>combien risquer par trade ?</b> Trop peu = pas de croissance. Trop = ruine. Kelly répond mathématiquement.</p>
        <h3>La formule Kelly de base</h3>
        <p><code>f* = (p × b - (1-p)) / b</code></p>
        <p>Où : <code>p</code> = probabilité de gagner, <code>b</code> = ratio gain/perte, <code>f*</code> = fraction optimale du capital à risquer.</p>
        <p><b>Exemple :</b> win rate 55 %, R:R 2:1 → f* = (0,55 × 2 - 0,45) / 2 = <b>32,5 %</b> du capital par trade. WTF ?</p>
        <h3>Pourquoi Full Kelly va vous ruiner</h3>
        <p>Kelly théorique suppose : (a) proba parfaitement connue, (b) rendements indépendants, (c) pas de fat tails. Dans la vraie vie : <b>aucune de ces conditions n&apos;est vraie</b>. Full Kelly = drawdowns catastrophiques.</p>
        <h3>La solution : Fractional Kelly</h3>
        <p>On applique un facteur d&apos;atténuation. Standard institutionnel : <b>Kelly / 4</b> ou <b>Kelly / 5</b>.</p>
        <p><b>Exemple précédent</b> : Kelly = 32,5 %, Kelly/4 = 8,1 %, Kelly/5 = 6,5 %. Toujours agressif — pour un compte demo. En live : divisez encore par 5 (soit ~1,3 % par trade).</p>
        <h3>Kelly multi-actifs (portfolio)</h3>
        <p>Si vous tradez 5 paires en même temps, il faut ajuster pour les corrélations. Si EURUSD et GBPUSD sont corrélés à 0,7, allouer 5 % à chacun ≠ 10 % au portfolio, c&apos;est 8,5 %. ANATRADEX calcule ça via l&apos;endpoint <code>POST /api/allocation/kelly-multi</code>.</p>
        <h3>En pratique sur ANATRADEX</h3>
        <p>Utilisez le <Link to="/app/sizer" className="text-accent-blue underline">Position Sizer</Link> avec votre win rate et R:R historiques. La calc applique Kelly/5 par défaut (agressif mais survivable). Pour un compte prop firm : réglez sur Kelly/10.</p>
        <p><b>Retenez :</b> une bonne stratégie mal sized fait perdre. Une stratégie moyenne bien sized survit et croît. Le sizing compte plus que le signal.</p>
      </>
    ),
  },
  {
    slug: "audit-cryptographique-pourquoi",
    level: "Pro",
    minutes: 5,
    title: "Pourquoi la chaîne d'audit SHA-256 change tout",
    summary: "Zero-trust et intégrité cryptographique : ce que ça implique pour votre propre traçabilité (attention : ANATRADEX ne remplace pas un reporting réglementé).",
    content: (
      <>
        <p>La plupart des plateformes retail vous demandent de <b>faire confiance à leurs chiffres</b>. « Notre Sharpe est de 2,4. » OK, mais comment le prouver ?</p>
        <h3>Le problème classique</h3>
        <p>Une plateforme peut :</p>
        <ul>
          <li>Modifier rétroactivement les signaux historiques (« oh, on a corrigé un bug »)</li>
          <li>Cacher les mauvais trades de l&apos;affichage marketing</li>
          <li>Ajuster les métriques présentées</li>
        </ul>
        <p>Vous n&apos;avez aucun moyen de le détecter. C&apos;est la <b>black box</b>.</p>
        <h3>La solution : hash-chain SHA-256</h3>
        <p>Chaque événement (signal, ordre, changement de config) est <b>immuablement scellé</b> avec :</p>
        <ol>
          <li>Un hash SHA-256 du contenu de l&apos;événement</li>
          <li>Un <code>prev_hash</code> qui pointe vers l&apos;événement précédent</li>
        </ol>
        <p>Résultat : si on modifie un événement passé, TOUS les hash suivants changent. Détection immédiate.</p>
        <h3>Zero-trust vérification</h3>
        <p>Sur <Link to="/audit" className="text-accent-blue underline">/audit</Link>, votre navigateur télécharge les bytes canoniques exacts et re-calcule chaque SHA-256. Aucune confiance en ANATRADEX n&apos;est requise — la crypto vérifie tout.</p>
        <p><b>Bonus démo :</b> cliquez sur « Simuler une falsification » pour voir la détection en direct.</p>
        <h3>Conformité réglementaire</h3>
        <ul>
          <li><b>Traçabilité des ordres</b> : la hash chain fournit la preuve d&apos;antériorité de vos décisions — utile pour vos propres audits internes.</li>
          <li><b>Reporting réglementé (MiFID II RTS 22, EMIR Article 9)</b> : ces obligations concernent uniquement les entités régulées (banques, brokers, entreprises d&apos;investissement). ANATRADEX, en tant qu&apos;outil d&apos;analyse en libre-service, n&apos;y est pas soumis et ne les substitue pas.</li>
          <li><b>SEC Rule 17a-4</b> : record retention immuable → SHA-256 = tamper-evident.</li>
        </ul>
        <p>Pour vous : si vous devez prouver l&apos;intégrité de votre historique (prop firm, investisseur, audit), la hash-chain est un <b>argument de crédibilité massif</b>. Aucun concurrent retail ne l&apos;offre.</p>
      </>
    ),
  },
];

// Merged articles source for AcademyArticle lookup (all tracks)
const ALL_ARTICLES = [...ARTICLES, ...CHARTISTE_ARTICLES, ...ALL_EXTRA_ARTICLES];

function ArticleCard({ article }) {
  const badgeColor = article.level === "Débutant" ? "bg-up/20 text-up border-up/40" :
                     article.level === "Semi-pro" ? "bg-accent-blue/20 text-accent-blue border-accent-blue/40" :
                     "bg-purple-500/20 text-purple-400 border-purple-500/40";
  return (
    <Link
      to={`/academy/${article.slug}`}
      className="group panel p-6 hover:border-accent-blue/50 transition-all"
      data-testid={`academy-card-${article.slug}`}
    >
      <div className="flex items-center gap-2 mb-4">
        <span className={`inline-block px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-semibold border ${badgeColor}`}>
          {article.level}
        </span>
        <span className="text-white/40 text-xs flex items-center gap-1"><Clock size={11} /> {article.minutes} min</span>
      </div>
      <h3 className="font-heading font-semibold text-xl leading-tight mb-3 group-hover:text-accent-blue transition-colors">
        {article.title}
      </h3>
      <p className="text-sm text-white/60 leading-relaxed">{article.summary}</p>
      <div className="mt-4 inline-flex items-center gap-1 text-xs text-accent-blue opacity-0 group-hover:opacity-100 transition-opacity">
        Lire l&apos;article <ArrowRight size={12} />
      </div>
    </Link>
  );
}

export default function Academy() {
  const [track, setTrack] = useState("all");
  const activeExtra = ALL_EXTRA_META.find((t) => t.id === track);
  const filteredArticles =
    track === "chartistes" ? CHARTISTE_ARTICLES :
    track === "quant"      ? ARTICLES :
    activeExtra            ? activeExtra.articles :
    ALL_ARTICLES;
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="academy-page">
      {/* NAV */}
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2" data-testid="academy-home-link">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div>
              <div className="font-heading font-bold text-lg leading-none">ANATRADEX</div>
              <div className="text-[10px] text-white/50 uppercase tracking-widest">Academy</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5" data-testid="academy-back-link">
              <ArrowLeft size={14} /> Retour
            </Link>
            <Link to="/signup" className="btn btn-primary text-xs px-4 py-2" data-testid="academy-signup-cta">
              Essai gratuit 14j
            </Link>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-accent-blue/10 border border-accent-blue/30 rounded-full text-xs uppercase tracking-widest text-accent-blue mb-4">
            <BookOpen size={12} /> Academy · Apprendre le quant sans jargon inutile
          </div>
          <h1 className="font-heading font-bold text-5xl md:text-6xl tracking-tight max-w-3xl">
            Du <span className="text-up">« ça marche »</span> au{" "}
            <span className="text-accent-blue">« statistiquement significatif »</span>.
          </h1>
          <p className="mt-6 text-lg text-white/60 max-w-2xl">
            Les concepts qu&apos;utilisent les prop-desks et les hedge funds — expliqués en 5-8 minutes.
            Aucun bullshit, aucun paywall.
          </p>
        </div>

        {/* Track selector — Quant / Chartistes */}
        <div className="flex flex-wrap items-center gap-2 mb-8 pb-4 border-b border-white/5" data-testid="academy-track-selector">
          <button
            onClick={() => setTrack("all")}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors ${track === "all" ? "bg-accent-blue/20 border-accent-blue/50 text-accent-blue" : "border-white/10 hover:border-white/30 text-white/70"}`}
            data-testid="academy-track-all"
          >
            📚 Tout ({ALL_ARTICLES.length})
          </button>          <button
            onClick={() => setTrack("quant")}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5 ${track === "quant" ? "bg-emerald-500/20 border-emerald-500/50 text-emerald-300" : "border-white/10 hover:border-white/30 text-white/70"}`}
            data-testid="academy-track-quant"
          >
            <Sigma size={13} /> Trading Quant / Algorithmique ({ARTICLES.length})
          </button>
          <button
            onClick={() => setTrack("chartistes")}
            className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5 ${track === "chartistes" ? "bg-purple-500/20 border-purple-500/50 text-purple-300" : "border-white/10 hover:border-white/30 text-white/70"}`}
            data-testid="academy-track-chartistes"
          >
            <BarChart3 size={13} /> Pour les Chartistes — Cours complet ({CHARTISTE_ARTICLES.length})
          </button>
          {ALL_EXTRA_META.map((tk) => (
            <button
              key={tk.id}
              onClick={() => setTrack(tk.id)}
              className={`px-4 py-2 rounded-md text-sm font-medium border transition-colors flex items-center gap-1.5 ${track === tk.id ? tk.activeClass : "border-white/10 hover:border-white/30 text-white/70"}`}
              data-testid={`academy-track-${tk.id}`}
            >
              <span aria-hidden>{tk.emoji}</span> {tk.label} ({tk.articles.length})
            </button>
          ))}
        </div>

        {/* Track description */}
        {track === "chartistes" && (
          <div className="panel p-4 mb-6 bg-purple-500/5 border-purple-500/20" data-testid="academy-chartistes-intro">
            <h3 className="text-lg font-semibold text-purple-300 mb-1">🎓 Cours complet — Analyse graphique du débutant au pro</h3>
            <p className="text-sm text-white/70">
              10 leçons progressives (~70 min de lecture au total). De la lecture d&apos;une bougie japonaise à la synthèse
              chartisme + quantitatif. Les articles sont ordonnés pédagogiquement — <b>suivez-les dans l&apos;ordre</b> pour le meilleur résultat.
            </p>
          </div>
        )}
        {activeExtra && (
          <div className={`panel p-4 mb-6 ${activeExtra.introClass}`} data-testid={`academy-${activeExtra.id}-intro`}>
            <h3 className={`text-lg font-semibold mb-1 ${activeExtra.introTitleClass}`}>{activeExtra.emoji} {activeExtra.label}</h3>
            <p className="text-sm text-white/70">{activeExtra.intro}</p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="academy-grid">
          {filteredArticles.map((a) => (
            <ArticleCard key={a.slug} article={a} />
          ))}
        </div>

        <div className="mt-20 text-center border-t border-white/10 pt-12">
          <h3 className="font-heading text-3xl mb-3">Prêt à appliquer ?</h3>
          <p className="text-white/60 mb-6">14 jours gratuits, sans carte bancaire.</p>
          <Link to="/signup" className="btn btn-primary text-base px-8 py-4" data-testid="academy-final-cta">
            Créer mon compte <TrendingUp size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

export function AcademyArticle() {
  const { slug } = useParams();
  const article = ALL_ARTICLES.find((a) => a.slug === slug);

  // Hooks must run before any conditional return — compute meta values unconditionally.
  // When article is missing they simply fall back to sensible ANATRADEX defaults; the
  // Navigate below then unmounts the component and usePageMeta restores original tags.
  const canonicalUrl = typeof window !== "undefined"
    ? `${window.location.origin}/academy/${slug || ""}`
    : `https://anatradex.com/academy/${slug || ""}`;
  const ogTitle = article
    ? `${article.title} — ANATRADEX Academy`
    : "ANATRADEX Academy";
  const ogDescription = article?.summary || "Ressource éducative ANATRADEX Academy.";
  const ogImage = typeof window !== "undefined"
    ? `${window.location.origin}/og-image.png`
    : "https://anatradex.com/og-image.png";
  usePageMeta({
    title: ogTitle,
    description: ogDescription,
    canonical: canonicalUrl,
    og: { title: ogTitle, description: ogDescription, url: canonicalUrl, image: ogImage, type: "article" },
    twitter: { title: ogTitle, description: ogDescription, image: ogImage, card: "summary_large_image" },
  });

  if (!article) return <Navigate to="/academy" replace />;

  const isPro = article.level === "Semi-pro" || article.level === "Pro";
  const ctaTarget = isPro ? "/beta" : "/signup";
  const ctaLabel = isPro ? "Postuler au programme beta" : "Créer mon compte";

  const badgeColor = article.level === "Débutant" ? "bg-up/20 text-up border-up/40" :
                     article.level === "Semi-pro" ? "bg-accent-blue/20 text-accent-blue border-accent-blue/40" :
                     "bg-purple-500/20 text-purple-400 border-purple-500/40";

  return (
    <div className="min-h-screen bg-bg text-white" data-testid={`academy-article-${slug}`}>
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/academy" className="flex items-center gap-2 text-white/60 hover:text-white" data-testid="article-back-link">
            <ArrowLeft size={16} /> Academy
          </Link>
          <Link to="/signup" className="btn btn-primary text-xs px-4 py-2" data-testid="article-signup-cta">
            Essai gratuit 14j
          </Link>
        </div>
      </nav>
      <article className="max-w-3xl mx-auto px-6 py-16">
        <div className="flex items-center gap-2 mb-6">
          <span className={`inline-block px-2 py-1 rounded text-[10px] uppercase tracking-widest font-semibold border ${badgeColor}`}>
            {article.level}
          </span>
          <span className="text-white/40 text-xs flex items-center gap-1"><Clock size={11} /> {article.minutes} min de lecture</span>
        </div>
        <h1 className="font-heading font-bold text-4xl md:text-5xl leading-tight tracking-tight mb-6">
          {article.title}
        </h1>
        <p className="text-lg text-white/60 mb-10 leading-relaxed">{article.summary}</p>

        {/* Social share bar — sticky between summary and content for visibility */}
        <ShareBar title={article.title} summary={article.summary} />

        <div className="prose-content space-y-5 text-white/80 leading-relaxed text-[15px]">
          {article.content}
        </div>

        {/* Second share bar at the end — proven pattern for higher share conversion */}
        <ShareBar title={article.title} summary={article.summary} />

        <div className="mt-16 pt-8 border-t border-white/10 text-center">
          <div className="text-sm text-white/50 mb-4">Continuer l&apos;apprentissage</div>
          <Link to="/academy" className="btn text-sm px-6 py-3 mr-3" data-testid="article-back-btn">
            Voir tous les articles
          </Link>
          <Link to={ctaTarget} className="btn btn-primary text-sm px-6 py-3" data-testid="article-final-cta">
            {ctaLabel} <TrendingUp size={14} />
          </Link>
        </div>
      </article>
    </div>
  );
}
